import type { Language, TranslationRecord } from '@masters-club/types';

type LanguageCode = Language['code'];
type TranslationsRecord = Record<string, TranslationRecord>;
type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export interface PricingTier {
  id: number;
  channelPassId: number | null;
  courseId: number | null;
  subscriptionPlanId: number | null;
  prices: Record<string, number>;
  baseCurrency: string;
  promoPrices?: Record<string, number> | null;
  promoUntil?: string | null;
  durationDays: number | null;
  isActive: boolean;
  order: number;

  imageId?: number | null;
  imageUrl?: string | null;
  gradientFrom?: string | null;
  gradientTo?: string | null;
  gradientAngle?: number;
  /**
   * Настройка цветов карточки абонемента (light/dark).
   * `null`/`undefined` — использовать дефолтные цвета на платформе.
   * Применяется только для тарифов подписочного плана.
   */
  cardAppearance?: {
    light: { bg: string; patternPrimary: string; patternSecondary: string };
    dark: { bg: string; patternPrimary: string; patternSecondary: string };
  } | null;
  isFamily: boolean;
  familySlots: number;
  translations?: Record<string, { name?: string }>;
  bonusTemplates?: BonusTemplate[];
  /** Доступы тарифа (новая модель: specific / quantity по COURSE/CHANNEL/BOOK). */
  entitlements?: PricingTierEntitlement[];
  /** Языки аудитории, для которых тариф виден. Пустой массив = виден всем. */
  targetLanguages?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface PricingTierEntitlement {
  id: number;
  pricingTierId: number;
  targetType: 'COURSE' | 'CHANNEL' | 'BOOK';
  targetId: number | null;
  selectionMode: 'specific' | 'quantity';
  quantity: number | null;
  filterCourseType: 'ONLINE' | 'MEETING' | 'OFFLINE' | null;
  accessDurationDays: number | null;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface BonusTemplate {
  id: number;
  tierId: number;
  type: 'NUMBER' | 'BOOLEAN' | 'ARRAY' | 'STRING';
  initialValue: JsonValue;
  durationMode: 'TIER' | 'CUSTOM' | 'PERMANENT' | 'ABSOLUTE';
  customDurationDays: number | null;
  expiresAt: string | null;
  order: number;
  translations?: Record<string, { name?: string; description?: string }>;
  createdAt: string;
}

export interface ChannelPass {
  id: number;
  channelId: number;
  userLimit: number | null;
  currentUserCount: number;
  saleEndDate: string | null;
  hasLimits: boolean;
  onLimitAction: 'HIDE_FROM_STORE' | 'DEACTIVATE' | 'TRIGGER';
  onLimitTriggerId: number | null;
  onLimitDelayDays: number;
  isActive: boolean;
  order: number;
  targetLanguages: string[];
  imageId?: number | null;
  imageUrl?: string | null;
  bannerImageId?: number | null;
  bannerImageUrl?: string | null;
  createdAt: string;
  updatedAt: string;
  translations?: TranslationsRecord;
  channel?: {
    id: number;
    title: string;
    username: string | null;
  };
  pricingTiers?: PricingTier[];
}

export interface ChannelPassFormData {
  channelId: number;
  userLimit: number | null;
  saleEndDate: string | null;
  hasLimits: boolean;
  onLimitAction: 'HIDE_FROM_STORE' | 'DEACTIVATE' | 'TRIGGER';
  onLimitTriggerId: number | null;
  onLimitDelayDays: number;
  isActive: boolean;
  targetLanguages: string[];
  imageId?: number | null;
  bannerImageId?: number | null;
  translations: Array<{
    languageCode: string;
    name: string;
    shortDescription?: string | null;
    description?: string | null;
  }>;
}

function pickTranslationName(record: unknown): string | undefined {
  if (
    record !== null &&
    typeof record === 'object' &&
    'name' in record &&
    typeof record.name === 'string' &&
    record.name.trim() !== ''
  ) {
    return record.name;
  }
  return undefined;
}

/**
 * Возвращает название доступа на запрошенном языке с fallback на другие локали.
 * Порядок: запрошенный язык → ru → en → первый язык с непустым name.
 * Только если имени нет ни в одной локали — `Доступ #id`.
 */
export function getChannelPassName(pass: ChannelPass, languageCode: LanguageCode = 'ru'): string {
  const translations = pass.translations;
  if (translations !== undefined && translations !== null && typeof translations === 'object') {
    for (const code of [languageCode, 'ru', 'en']) {
      const name = pickTranslationName(translations[code]);
      if (name !== undefined) {
        return name;
      }
    }

    // Последний шанс — любой язык с непустым именем
    for (const record of Object.values(translations)) {
      const name = pickTranslationName(record);
      if (name !== undefined) {
        return name;
      }
    }
  }
  return `Доступ #${pass.id}`;
}

export function formatPrice(price: string | number, currency: string): string {
  const numPrice = typeof price === 'number' ? price : parseFloat(price);
  if (isNaN(numPrice)) {
    return `${price} ${currency}`;
  }
  return new Intl.NumberFormat('ru-RU').format(numPrice) + ` ${currency}`;
}

export function formatPriceRange(tiers: PricingTier[]): string {
  const activeTiers = tiers.filter((t) => t.isActive);
  if (activeTiers.length === 0) {
    return '—';
  }

  const basePrices = activeTiers
    .map((t) => t.prices[t.baseCurrency])
    .filter((p): p is number => p !== undefined && p !== null);

  if (basePrices.length === 0) {
    return '—';
  }

  const baseCurrency = activeTiers[0].baseCurrency;
  const min = Math.min(...basePrices);
  const max = Math.max(...basePrices);

  if (min === max) {
    return formatPrice(min, baseCurrency);
  }

  return `${new Intl.NumberFormat('ru-RU').format(min)} – ${new Intl.NumberFormat('ru-RU').format(max)} ${baseCurrency}`;
}

export const LIMIT_ACTION_OPTIONS = [
  { value: 'HIDE_FROM_STORE', label: 'Скрыть из магазина' },
  { value: 'DEACTIVATE', label: 'Деактивировать' },
  { value: 'TRIGGER', label: 'Запустить триггер' },
] as const;

export function getLimitActionLabel(action: string): string {
  return LIMIT_ACTION_OPTIONS.find((opt) => opt.value === action)?.label ?? action;
}
